import React, { useState } from "react";
import axios from "axios";
import { PRACTICE_AREA_OPTIONS } from "../../data/practiceAreas";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  full_name: "",
  email: "",
  phone: "",
  company: "",
  practice_area: "",
  message: "",
};

export default function ContactForm({ sourcePage = "", defaultArea = "" }) {
  const [form, setForm] = useState({ ...EMPTY, practice_area: defaultArea });
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("submitting");
    setError("");
    try {
      await axios.post(`${API}/leads`, {
        ...form,
        source_page: sourcePage,
      });
      setStatus("success");
      setForm({ ...EMPTY });
    } catch (err) {
      setStatus("error");
      setError(
        err?.response?.data?.detail?.[0]?.msg ||
          "Something went wrong. Please check your details and try again."
      );
    }
  };

  if (status === "success") {
    return (
      <div
        className="border border-hairline bg-white rounded-[4px] p-8 text-center"
        data-testid="contact-form-success"
      >
        <h3 className="text-xl mb-2">Thank you — your request has been received.</h3>
        <p className="text-subink mb-6">
          A member of our team will follow up shortly with pricing and lead
          availability for your practice area.
        </p>
        <button
          type="button"
          className="text-gold font-semibold underline underline-offset-2 hover:text-navy"
          onClick={() => setStatus("idle")}
          data-testid="contact-form-reset"
        >
          Submit another request
        </button>
      </div>
    );
  }

  const inputClass =
    "w-full border border-hairline rounded-[4px] px-3 py-2.5 text-[15px] text-ink bg-white focus:outline-none focus:border-navy transition-colors";
  const labelClass = "block text-sm font-medium text-ink mb-1.5";

  return (
    <form
      onSubmit={handleSubmit}
      className="border border-hairline bg-white rounded-[4px] p-6 sm:p-8"
      data-testid="contact-form"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelClass} htmlFor="full_name">Full Name</label>
          <input
            id="full_name" name="full_name" type="text" required
            value={form.full_name} onChange={handleChange}
            className={inputClass} data-testid="contact-full-name"
          />
        </div>
        <div>
          <label className={labelClass} htmlFor="email">Email</label>
          <input
            id="email" name="email" type="email" required
            value={form.email} onChange={handleChange}
            className={inputClass} data-testid="contact-email"
          />
        </div>
        <div>
          <label className={labelClass} htmlFor="phone">Phone</label>
          <input
            id="phone" name="phone" type="tel" required
            value={form.phone} onChange={handleChange}
            className={inputClass} data-testid="contact-phone"
          />
        </div>
        <div>
          <label className={labelClass} htmlFor="company">Firm / Company Name</label>
          <input
            id="company" name="company" type="text" required
            value={form.company} onChange={handleChange}
            className={inputClass} data-testid="contact-company"
          />
        </div>
      </div>

      <div className="mt-4">
        <label className={labelClass} htmlFor="practice_area">Practice Area</label>
        <select
          id="practice_area" name="practice_area" required
          value={form.practice_area} onChange={handleChange}
          className={inputClass} data-testid="contact-practice-area"
        >
          <option value="" disabled>Select a practice area</option>
          {PRACTICE_AREA_OPTIONS.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </div>

      <div className="mt-4">
        <label className={labelClass} htmlFor="message">Message</label>
        <textarea
          id="message" name="message" rows={4}
          value={form.message} onChange={handleChange}
          className={inputClass} data-testid="contact-message"
          placeholder="Tell us about your firm and the volume or geography you're targeting."
        />
      </div>

      {status === "error" && (
        <p className="text-destructive text-sm mt-4" data-testid="contact-form-error">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={status === "submitting"}
        className="mt-6 w-full sm:w-auto bg-gold text-navy font-semibold px-6 py-3 rounded-[4px] hover:bg-navy hover:text-white transition-colors disabled:opacity-60"
        data-testid="contact-submit"
      >
        {status === "submitting" ? "Submitting…" : "Request Pricing & Availability"}
      </button>
    </form>
  );
}
